import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRealtimeUnits, useRealtimeDrivers } from 'Core/hooks';
import { useIntl } from 'react-intl';
import cx from 'classnames';

// Styles
import './styles.scss';

// Custom Hook
import useEntityData from './hooks/useEntityData';

// Components
import EntityTypeSelector from './components/EntityTypeSelector';
import OptionSelector from './components/OptionSelector';
import InactiveDriversToggle from './components/InactiveDriversToggle';
import ContentArea from './components/ContentArea';

/**
 * Selector for entities (Units/Drivers) with modes: "all", "entities", or "tags"
 * 
 * @param {string} props.useIncludesInactive - Controls when inactive drivers toggle is shown.
 *   Values: 'Driver-all' or 'Driver-hideTags' (above options), 'Driver-entities' (in content area)
 */
const EntityUnitDriverSelector = (props) => {
    const { messages } = useIntl();
    const {
        entities,
        entity,
        option,
        entityLabel,
        onChange,
        disabled,
        values,
        entityLabelClass,
        entityButtonClass,
        entityOptionLabelClass,
        entityOptionButtonClass,
        entityTagPickerContainerClass,
        onOpen,
        includeInactiveDrivers,
        hiddenCheckbox,
        useIncludesInactive,
        onOptionChangeStatus,
        includeUnassignedDriver,
    } = props;

    // Redux selectors
    const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
    const units = useRealtimeUnits({ trackFields: ['id', 'name', 'label', 'disabled', 'driverId', 'vehicleStatusId', 'tags'] });

    // Local state
    const [searchText, setSearchText] = useState("");

    // Generate unique ID for toggle checkboxes
    const toggleId = useMemo(() => {
        return `entity-tag-picker-toggle-${entity?.id || 'default'}-${option?.id || 'default'}`;
    }, [entity?.id, option?.id]);

    // Custom hook for data filtering and search logic
    const [data, total] = useEntityData({
        entity,
        option,
        includeInactiveDrivers,
        includeUnassignedDriver,
        searchText,
        drivers,
        units
    });

    // Determine if inactive drivers toggle should be shown above options
    const showInactiveToggleAbove = ['Driver-all', 'Driver-hideTags'].includes(useIncludesInactive);
    
    // Determine if inactive drivers toggle should be shown in content area
    const showInactiveToggleInContent = useIncludesInactive === 'Driver-entities';

    return (
        <div>
            <div>
                {/* Entity Label */}
                {entityLabel && (
                    <div className={cx("entity-tag-picker-label", entityLabelClass)}>
                        {entityLabel}
                    </div>
                )}

                {/* Entity Type Selector (Unit/Driver) */}
                <EntityTypeSelector
                    entities={entities}
                    entity={entity}
                    onChangeEntity={props.onChangeEntity}
                    onChange={onChange}
                    entityButtonClass={entityButtonClass}
                    messages={messages}
                />

                {/* Inactive Drivers Toggle (Above Options) */}
                {showInactiveToggleAbove && (
                    <InactiveDriversToggle
                        value={includeInactiveDrivers}
                        onChange={onOptionChangeStatus}
                        entityOptionLabelClass={entityOptionLabelClass}
                        toggleId={toggleId}
                        messages={messages}
                    />
                )}

                {/* Option Selector (All/Entities/Tags) */}
                <OptionSelector
                    option={option}
                    entity={entity}
                    onOptionChange={props.onOptionChange}
                    onChange={onChange}
                    onSearchTextChange={setSearchText}
                    entityOptionButtonClass={entityOptionButtonClass}
                    messages={messages}
                />

                {/* Content Area */}
                <div className={cx("entity-unit-driver-selector__content-area", entityTagPickerContainerClass)}>
                    {/* Inactive Drivers Toggle (In Content Area) */}
                    {showInactiveToggleInContent && (
                        <InactiveDriversToggle
                            value={includeInactiveDrivers}
                            onChange={onOptionChangeStatus}
                            entityOptionLabelClass={entityOptionLabelClass}
                            toggleId={toggleId}
                            messages={messages}
                        />
                    )}

                    {/* Content Area Router */}
                    <ContentArea
                        option={option}
                        entity={entity}
                        messages={messages}
                        searchText={searchText}
                        setSearchText={setSearchText}
                        data={data}
                        total={total}
                        values={values}
                        onChange={onChange}
                        disabled={disabled}
                        onOpen={onOpen}
                        hiddenCheckbox={hiddenCheckbox}
                        includeInactiveDrivers={includeInactiveDrivers}
                    />
                </div>
            </div>
        </div>
    );
};

export default EntityUnitDriverSelector;
